import React from "react";
import { NavLink, Navigate, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, UserCheck, LogOut, Settings, UsersRound, Package, FileText } from "lucide-react";
import { useAuth } from "../../lib/auth";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/leads", label: "Leads", icon: Users },
  { to: "/admin/customers", label: "Customers", icon: UserCheck },
  { to: "/admin/invoices", label: "Invoices", icon: FileText },
  { to: "/admin/services", label: "Services", icon: Package, superAdminOnly: true },
  { to: "/admin/operators", label: "Operators", icon: UsersRound, superAdminOnly: true },
];

export default function AdminLayout() {
  const { user, loading, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const admin = user; // alias used by older references in this file

  if (loading) return <div className="min-h-screen grid place-items-center text-[#5C6661]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/portal" replace />;

  const onLogout = () => { logout(); navigate("/login"); };
  const visibleNav = NAV.filter((n) => !n.superAdminOnly || admin.role === "super_admin");

  return (
    <div className="min-h-screen flex bg-[#F3F1EC]" data-testid="admin-layout">
      <aside className="hidden md:flex w-64 flex-col bg-[#FDFCFB] border-r border-[#E5E2DC]" data-testid="admin-sidebar">
        <div className="px-6 py-6 flex items-center gap-2 border-b border-[#E5E2DC]">
          <div className="w-9 h-9 rounded-xl bg-[#1E4D3B] grid place-items-center text-[#FDFCFB] font-display font-semibold">F</div>
          <div>
            <div className="font-display font-semibold text-[#121C17]">FinSahayak</div>
            <div className="text-xs text-[#5C6661]">CRM</div>
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {visibleNav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`sidebar-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  isActive ? "bg-[#121C17] text-[#FDFCFB]" : "text-[#5C6661] hover:text-[#121C17] hover:bg-[#F3F1EC]"
                }`
              }>
              <n.icon size={16} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-[#E5E2DC]">
          <div className="px-4 py-3 rounded-xl bg-[#F3F1EC]">
            <div className="text-sm font-medium text-[#121C17] truncate" data-testid="admin-name">{admin.name}</div>
            <div className="text-xs text-[#5C6661] truncate">{admin.email}</div>
          </div>
          <button onClick={onLogout} data-testid="admin-logout"
            className="mt-3 w-full flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm text-[#5C6661] hover:text-[#121C17] hover:bg-[#F3F1EC]">
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-30 glass-nav border-b border-[#E5E2DC]" data-testid="admin-mobile-topbar">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-[#1E4D3B] grid place-items-center text-[#FDFCFB] font-display font-semibold text-sm">F</div>
            <div className="leading-tight">
              <div className="font-display font-semibold text-sm text-[#121C17]">FinSahayak CRM</div>
              <div className="text-[10px] text-[#5C6661]">{admin.name}</div>
            </div>
          </div>
          <button onClick={onLogout} className="rounded-full p-2 hover:bg-[#F3F1EC]" data-testid="admin-mobile-logout" aria-label="Sign out">
            <LogOut size={16} className="text-[#5C6661]" />
          </button>
        </div>
      </div>

      {/* Mobile bottom nav */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-[#E5E2DC] flex justify-around py-2 shadow-[0_-8px_24px_rgba(18,28,23,0.06)]" data-testid="admin-mobile-nav">
        {visibleNav.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end}
            className={({ isActive }) => `flex flex-col items-center gap-1 text-[11px] px-3 py-1 transition-colors ${isActive ? "text-[#1E4D3B] font-semibold" : "text-[#5C6661]"}`}>
            {({ isActive }) => (
              <>
                <span className={`w-9 h-9 rounded-full grid place-items-center transition-colors ${isActive ? "bg-[#1E4D3B]/10" : ""}`}>
                  <n.icon size={18} />
                </span>
                {n.label}
              </>
            )}
          </NavLink>
        ))}
      </div>

      <main className="flex-1 overflow-x-hidden pt-14 md:pt-0 pb-24 md:pb-0">
        <Outlet />
      </main>
    </div>
  );
}
